"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.appName = exports.AuthenticatedPlayer = exports.Roles = exports.SessionUser = exports.getAuth = exports.initializeAuth = void 0;
const app_1 = __importDefault(require("firebase/compat/app"));
require("firebase/compat/auth");
const config_1 = __importDefault(require("./config"));
const auth_entities_1 = require("./auth-entities");
Object.defineProperty(exports, "SessionUser", { enumerable: true, get: function () { return auth_entities_1.SessionUser; } });
Object.defineProperty(exports, "Roles", { enumerable: true, get: function () { return auth_entities_1.Roles; } });
Object.defineProperty(exports, "AuthenticatedPlayer", { enumerable: true, get: function () { return auth_entities_1.AuthenticatedPlayer; } });
class AuthService {
    constructor(axios) {
        /**
         * Returns an access token used to identify the user to a Rafflebox service.
         *
         * @remarks
         * Returns the current token if it has not expired or if it will not expire in the next five
         * minutes. Otherwise, this will refresh the token and return a new one.
         */
        this.verifyToken = (forceRefresh = undefined) => __awaiter(this, void 0, void 0, function* () {
            const signedInUser = yield this.currentUser();
            return signedInUser ? signedInUser.getIdToken(forceRefresh) : undefined;
        });
        /**
         * Signs in using `email` and `password`.
         */
        this.signIn = (email, password, params) => __awaiter(this, void 0, void 0, function* () {
            const body = {
                email,
                password,
                deviceSerialNumber: params === null || params === void 0 ? void 0 : params.deviceSerialNumber,
                deviceAPKVersion: params === null || params === void 0 ? void 0 : params.deviceAPKVersion,
                deviceAppUrl: params === null || params === void 0 ? void 0 : params.deviceAppUrl,
                deviceWebAppCommitHash: params === null || params === void 0 ? void 0 : params.deviceWebAppCommitHash
            };
            let requestConfig = {
                headers: {}
            };
            if (params === null || params === void 0 ? void 0 : params.client) {
                requestConfig = {
                    headers: {
                        client: params.client
                    }
                };
            }
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            const response = appName.includes('PlayerPortal')
                ? yield this.axios.post('/player-service/v2/token', body, requestConfig)
                : yield this.axios.post('/auth/token', body, requestConfig);
            const customToken = response.data.data.token;
            yield app_1.default.auth().signInWithCustomToken(customToken);
        });
        /**
         * Switches to an organization specified by `organizationId`. The user must be already signed in.
         */
        this.switchOrganizations = (organizationId) => __awaiter(this, void 0, void 0, function* () {
            const token = yield this.changeOrganization(organizationId);
            yield app_1.default.auth().signInWithCustomToken(token);
        });
        /**
         * Signs out the current user.
         */
        this.signOut = () => __awaiter(this, void 0, void 0, function* () {
            yield app_1.default.auth().signOut();
        });
        /**
         * Checks a verification code sent to the user by email or other out-of-band mechanism.
         */
        this.checkActionCode = (code) => __awaiter(this, void 0, void 0, function* () {
            return app_1.default.auth().checkActionCode(code);
        });
        /**
         * Obtains the Firebase custom token for a different organization specified by `organizationId` that can be later exchanged for an access token.
         * The user must be already signed in.
         */
        this.changeOrganization = (organizationId) => __awaiter(this, void 0, void 0, function* () {
            const response = yield this.axios.post(`/auth-service/v2/change-organization`, { organizationId: organizationId });
            return response.data.data.token;
        });
        /**
         * Adds an observer for changes to the user's sign-in state.
         *
         * @remarks
         * To keep the old behavior, see {@link Auth.onIdTokenChanged}.
         *
         * @param nextOrObserver - callback triggered on change.
         * @param error - callback triggered on error.
         * @param completed - callback triggered when observer is removed.
         */
        this.onAuthStateChanged = app_1.default.auth().onAuthStateChanged.bind(app_1.default.auth());
        this.axios = axios;
    }
    /**
     * The currently signed-in user (or undefined).
     */
    sessionUser() {
        return __awaiter(this, void 0, void 0, function* () {
            const user = yield this.currentUser();
            const idTokenResult = yield (user === null || user === void 0 ? void 0 : user.getIdTokenResult());
            if (idTokenResult) {
                // eslint-disable-next-line @typescript-eslint/no-use-before-define
                return appName.includes('PlayerPortal') ? new auth_entities_1.AuthenticatedPlayer(idTokenResult) : new auth_entities_1.SessionUser(idTokenResult);
            }
            return undefined;
        });
    }
    /**
     * The currently signed-in user (or null).
     */
    currentUser() {
        return __awaiter(this, void 0, void 0, function* () {
            return new Promise((resolve, reject) => {
                /**
                 * The Firebase JavaScript SDK gets reinitialized upon a WEB page refresh.
                 * Thus the returned value could be `null` even when a user is signed in.
                 * The solution to the problem is to use `onAuthStateChanged` method instead.
                 *
                 * More information about the issue could be found [here](https://github.com/firebase/firebase-js-sdk/issues/462)
                 */
                if (app_1.default.auth().currentUser) {
                    resolve(app_1.default.auth().currentUser);
                }
                const unsubscribe = app_1.default.auth().onAuthStateChanged((user) => {
                    unsubscribe();
                    resolve(user);
                }, reject);
            });
        });
    }
}
let instance;
let appName;
exports.appName = appName;
/**
 * Creates and initializes an `AuthService` instance.
 *
 * The following app names are supported:
 *  - `development`
 *  - `test`
 *  - `production`
 *  - `testPlayerPortal`
 *  - `productionPlayerPortal`
 */
const initializeAuth = (env, axios) => {
    app_1.default.initializeApp(config_1.default[env]);
    exports.appName = appName = env;
    instance = new AuthService(axios);
};
exports.initializeAuth = initializeAuth;
/**
 * Returns an `AuthService` instance.
 */
const getAuth = () => {
    return instance;
};
exports.getAuth = getAuth;
//# sourceMappingURL=data:application/json;base64,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